import React from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableContainer, TableHead, Paper, TableRow, TableCell } from '@mui/material';
import { ObjLimitationPlant, ObjLimitationSerie, WKZNamePlant } from '@/types';
import { equalObjs } from '@/helpers/utils';
import AffectedHeaderRow from '../AffectedHeaderRow/AffectedHeaderRow';
import SeriesRow from '../SeriesRow/SeriesRow';
import PlantsRow from '../SeriesRow/PlantsRow';

type SeriesTableProps = {
  modelSeries?: string[];
  options?: { plants: WKZNamePlant[] }
  selections: ObjLimitationPlant[] | ObjLimitationSerie[];
  onRowsUpdate: (selections: ObjLimitationPlant[] | ObjLimitationSerie[]) => void; // eslint-disable-line no-unused-vars
  changedRows?: (changedIn: string) => void; // eslint-disable-line no-unused-vars
  checkEnabled: boolean;
  isEditable: boolean;
  isSeries?: boolean;
  handleDeleteSeries?: (serie: string) => void; // eslint-disable-line no-unused-vars
  handleDeletePlants?: (wkzNumber: string) => void; // eslint-disable-line no-unused-vars
};

const SeriesTable: React.FC<SeriesTableProps> = (props) => {
  const { modelSeries, options, selections, onRowsUpdate = () => { }, changedRows = () => { }, checkEnabled, isEditable,
    isSeries = false, handleDeleteSeries, handleDeletePlants } = props;
  const { t } = useTranslation();

  const handleCheckAll = (checked: boolean) => {
    const checkSelections = selections.map((selection) => ({ ...selection, checked }));

    onRowsUpdate(checkSelections);
  };

  const handleRowUpdate = (index: number, update) => {
    const afterUpdate = [
      ...selections.slice(0, index),
      update,
      ...selections.slice(index + 1),
    ];

    onRowsUpdate(afterUpdate);

    if (!equalObjs(selections[index], update, 'checked')) changedRows('series');
  };

  return (
    <TableContainer component={Paper}>
      <Table aria-label="Series">
        <TableHead>
          <AffectedHeaderRow
            isEditable={isEditable}
            checked={selections.length > 0 && selections.every(({ checked }) => checked)}
            onCheckAll={handleCheckAll}
            isSeries={isSeries}
          />
        </TableHead>
        <TableBody>
          {(selections?.length > 0)
            ? selections.map((selection: ObjLimitationSerie | ObjLimitationPlant, index: number) => (
              isSeries ? (
                <SeriesRow
                  index={index}
                  key={(selection as ObjLimitationSerie)?.serie || 'new-series'}
                  isEditable={isEditable}
                  checkEnabled={checkEnabled}
                  modelSeries={modelSeries as string[]}
                  selection={selection as ObjLimitationSerie}
                  onRowUpdate={(update) => handleRowUpdate(index, update)}
                  handleDeleteSeries={handleDeleteSeries as (serie: string) => void} // eslint-disable-line no-unused-vars
                />
              ) : (
                <PlantsRow
                  key={(selection as ObjLimitationPlant)?.plantName || 'new-plant'}
                  index={index}
                  isEditable={isEditable}
                  checkEnabled={checkEnabled}
                  options={options as { plants: WKZNamePlant[] }}
                  selection={selection as ObjLimitationPlant}
                  handleDeletePlants={handleDeletePlants as (wkzNumber: string) => void} // eslint-disable-line no-unused-vars
                />
              )))
            : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {t('noResults')}
                </TableCell>
              </TableRow>
            )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SeriesTable;
